import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Container, Grid, Stack, Table, TableBody, TableHead, TableRow } from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { useNavigate, useParams } from 'react-router-dom';
import AnalysisTabBar from '../components/AnalysisTabBar';
import NorthIndianChart from '../components/charts/NorthIndianChart';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type DashaPeriod = {
  level?: string;
  lord: string;
  parentLord?: string;
  parentLevel?: string;
  startUtc: string;
  endUtc: string;
  yearsWeight: number;
  antardashas?: DashaPeriod[];
  pratyantardashas?: DashaPeriod[];
};

type TpReport = {
  selectedYear: number;
  model: string;
  method: string;
  tpStartUtc: string;
  nextTpStartUtc: string;
  tpStartLocal: string;
  nextTpStartLocal: string;
  timeZoneUsed: string;
  locationUsed?: { city?: string; country?: string; latitude?: number; longitude?: number };
  natalAngle: number;
  tithi?: { number: number; name: string; paksha: string; birthAngle: number };
  vara?: { hinduWeekday: string; varaLord: string; horaLord: string; horaNumber: number; horaType: string; sunriseLocal: string; sunsetLocal: string; nextSunriseLocal: string };
  dasha?: DashaPeriod[];
  chart?: { planets?: Record<string, any>; houses?: any; calculationData?: any };
  notes?: string[];
};

const currentYear = new Date().getFullYear();
const formatNumber = (value?: number) => Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '-';
const formatClock = (hour: number, minute: number) => {
  const period = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
};
const formatTithiDateTime = (value?: string, options: { utc?: boolean; keepSuffix?: boolean } = {}) => {
  if (!value) return '-';
  const raw = String(value);
  const localMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:\s+(.+))?$/);
  if (localMatch) {
    const [, year, month, day, hour, minute, suffix] = localMatch;
    return `${day}/${month}/${year} ${formatClock(Number(hour), Number(minute))}${options.keepSuffix && suffix ? ` ${suffix}` : ''}`;
  }
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    const day = options.utc ? date.getUTCDate() : date.getDate();
    const month = (options.utc ? date.getUTCMonth() : date.getMonth()) + 1;
    const year = options.utc ? date.getUTCFullYear() : date.getFullYear();
    const hour = options.utc ? date.getUTCHours() : date.getHours();
    const minute = options.utc ? date.getUTCMinutes() : date.getMinutes();
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year} ${formatClock(hour, minute)}${options.utc ? ' UTC' : ''}`;
  }
  return raw;
};
const formatDashaDate = (value?: string) => formatTithiDateTime(value, { utc: true }).replace(/\sUTC$/, '');
const dashaKey = (item?: DashaPeriod | null) => `${item?.lord || '-'}-${item?.startUtc || ''}`;
const PLANET_ORDER = ['ASC', 'SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];
const NADI_PLANETS_ORDER = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'];
const shortPlanet = (value?: any) => {
  const raw = String(value || '-').trim();
  const map: Record<string, string> = {
    ASCENDANT: 'Asc',
    SUN: 'Su',
    MOON: 'Mo',
    MARS: 'Ma',
    MERCURY: 'Me',
    JUPITER: 'Ju',
    VENUS: 'Ve',
    SATURN: 'Sa',
    RAHU: 'Ra',
    KETU: 'Ke',
  };
  return map[raw.toUpperCase()] || raw.slice(0, 3);
};
const planetKey = (value?: any) => String(value || '').trim().toUpperCase();
const arr = (value: any) => Array.isArray(value) ? value.map((item) => Number(item)).filter((item) => Number.isFinite(item)) : [];
const unique = (values: number[]) => Array.from(new Set(values));
const degreeText = (value: any) => {
  if (value?.formatted) return value.formatted;
  if (typeof value === 'number') return `${(((value % 30) + 30) % 30).toFixed(2)}°`;
  return value || '-';
};
const buildKpPlanetRows = (chartData: any) => {
  const kp = chartData?.kpAnalysis || {};
  const sigMap = kp.kpPlanetarySignificators || {};
  const sigEntries = Object.values(sigMap) as any[];
  const planets = chartData?.planets || {};
  const rows: any[] = [];
  const asc = chartData?.houses?.ascendant;
  const ascCusp = kp.kpCusps?.[1] || kp.kpCusps?.['1'] || {};
  if (asc) {
    rows.push({
      key: 'ASC',
      planet: 'Asc',
      planetName: 'ASC',
      starLordName: ascCusp.starLord || asc.nakshatra?.lord,
      subLordName: ascCusp.subLord || asc.subLord,
      rl: shortPlanet(ascCusp.cuspLord || asc.sign?.ruler),
      nl: shortPlanet(ascCusp.starLord || asc.nakshatra?.lord),
      sl: shortPlanet(ascCusp.subLord || asc.subLord),
      ssl: shortPlanet(ascCusp.subSubLord),
      houses: arr(ascCusp.significators || ascCusp.houseSignificators || ascCusp.allSignificators),
    });
  }

  Object.entries(planets)
    .sort(([a], [b]) => {
      const ai = PLANET_ORDER.indexOf(planetKey(a));
      const bi = PLANET_ORDER.indexOf(planetKey(b));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .forEach(([key, planet]: [string, any]) => {
      const canonical = planetKey(planet?.name || key);
      const entry = sigMap[canonical] || sigEntries.find((item: any) => planetKey(item?.planet) === canonical) || {};
      const level1 = arr(entry.level1?.length ? entry.level1 : entry.levels?.starLord?.significators);
      const level2 = arr(entry.level2?.length ? entry.level2 : entry.levels?.planet?.significators);
      const level3 = arr(entry.level3?.length ? entry.level3 : entry.levels?.starLordOwnership?.significators);
      const level4 = arr(entry.level4?.length ? entry.level4 : entry.levels?.planetOwnership?.significators);
      rows.push({
        key: canonical,
        planet: planet?.name || key,
        planetName: planet?.name || key,
        starLordName: entry.starLord || planet?.nakshatra?.lord,
        subLordName: entry.subLord || planet?.subLord,
        rl: shortPlanet(planet?.sign?.ruler),
        nl: shortPlanet(entry.starLord || planet?.nakshatra?.lord),
        sl: shortPlanet(entry.subLord || planet?.subLord),
        ssl: shortPlanet(entry.subSubLord || entry.levels?.subSubLord?.name),
        houses: arr(entry.allSignificators?.length ? entry.allSignificators : unique([...level1, ...level2, ...level3, ...level4])),
      });
    });
  return rows;
};
const buildNakshatraNadiRows = (planetRows: any[]) => {
  const rowByPlanet = new Map<string, any>();
  const starLordKeys = new Set<string>();
  planetRows.forEach((row) => {
    rowByPlanet.set(planetKey(row.planetName || row.planet), row);
    if (row.starLordName) starLordKeys.add(planetKey(row.starLordName));
  });

  return NADI_PLANETS_ORDER.map((key) => {
    const row = rowByPlanet.get(planetKey(key));
    if (!row) return null;
    const starLordRow = rowByPlanet.get(planetKey(row.starLordName));
    const subLordRow = rowByPlanet.get(planetKey(row.subLordName));
    return {
      key,
      planet: row.planetName || row.planet,
      planetSigs: row.houses || [],
      starLord: row.starLordName,
      starLordSigs: starLordRow?.houses || [],
      subLord: row.subLordName,
      subLordSigs: subLordRow?.houses || [],
      isUntenanted: !starLordKeys.has(planetKey(row.planetName || row.planet)),
    };
  }).filter(Boolean);
};
const buildKpHouseRows = (chartData: any) => {
  const kp = chartData?.kpAnalysis || {};
  const houseSignificators = kp.houseSignificators || {};
  const kpCusps = kp.kpCusps || {};
  const cusps = chartData?.houses?.cusps || {};
  return Array.from({ length: 12 }, (_, index) => {
    const house = index + 1;
    const cusp = kpCusps[house] || kpCusps[String(house)] || {};
    const houseData = cusps[house] || cusps[String(house)] || {};
    const links = houseSignificators[house] || houseSignificators[String(house)] || cusp.significators || cusp.houseSignificators || cusp.allSignificators || [];
    return {
      house,
      degree: degreeText(houseData.degree || cusp.degree || houseData.longitude || cusp.longitude),
      cuspLord: shortPlanet(cusp.cuspLord || houseData.sign?.ruler),
      starLord: shortPlanet(cusp.starLord || houseData.nakshatra?.lord),
      subLord: shortPlanet(cusp.subLord || houseData.subLord),
      subSubLord: shortPlanet(cusp.subSubLord),
      significators: arr(links),
    };
  });
};

function AnnualDashaCard({ dasha, language }: { dasha: DashaPeriod[]; language: string }) {
  const [expandedMahadashaKey, setExpandedMahadashaKey] = useState('');
  const [expandedAntardashaKey, setExpandedAntardashaKey] = useState('');

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{translateInline(language, 'One Year Vimshottari Dasha')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translateInline(language, 'Select a Mahadasha to open Antardasha, then select Antardasha to open Pratyantar Dasha.')}
        </Typography>
        <Stack spacing={0.75}>
          {dasha.length ? dasha.map((mahadasha) => {
            const mdKey = dashaKey(mahadasha);
            const expandedMd = expandedMahadashaKey === mdKey;
            return (
              <Box key={mdKey}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setExpandedMahadashaKey(expandedMd ? '' : mdKey);
                    setExpandedAntardashaKey('');
                  }}
                  sx={{
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    py: 1.2,
                    borderColor: expandedMd ? 'primary.main' : 'divider',
                    bgcolor: expandedMd ? 'rgba(46, 125, 82, 0.08)' : 'background.paper',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography component="span" sx={{ fontWeight: 800, color: expandedMd ? 'primary.main' : 'text.primary' }}>{mahadasha.lord || '-'}</Typography>
                    <Typography component="span" color="text.secondary" sx={{ fontSize: 12 }}>{formatDashaDate(mahadasha.startUtc)} - {formatDashaDate(mahadasha.endUtc)}</Typography>
                  </Stack>
                  <Typography component="span" color="text.secondary">{expandedMd ? '▲' : '▶'}</Typography>
                </Button>
                {expandedMd ? (
                  <Stack spacing={0.5} sx={{ mt: 0.75, ml: 2 }}>
                    {(mahadasha.antardashas || []).map((antardasha) => {
                      const adKey = dashaKey(antardasha);
                      const expandedAd = expandedAntardashaKey === adKey;
                      return (
                        <Box key={adKey}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setExpandedAntardashaKey(expandedAd ? '' : adKey)}
                            sx={{
                              justifyContent: 'space-between',
                              textAlign: 'left',
                              py: 0.9,
                              borderColor: expandedAd ? '#8B5CF6' : 'divider',
                              bgcolor: expandedAd ? '#8B5CF610' : 'background.paper',
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                              <Typography component="span" sx={{ fontWeight: 700, color: expandedAd ? '#8B5CF6' : 'text.primary' }}>{antardasha.lord || '-'}</Typography>
                              <Typography component="span" color="text.secondary" sx={{ fontSize: 12 }}>{formatDashaDate(antardasha.startUtc)} - {formatDashaDate(antardasha.endUtc)}</Typography>
                            </Stack>
                            <Typography component="span" color="text.secondary">{expandedAd ? '▲' : '▶'}</Typography>
                          </Button>
                          {expandedAd ? (
                            <Stack spacing={0.5} sx={{ mt: 0.5, ml: 2 }}>
                              {(antardasha.pratyantardashas || []).map((pratyantardasha) => (
                                <Box
                                  key={dashaKey(pratyantardasha)}
                                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1.5, py: 0.85, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
                                >
                                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{pratyantardasha.lord || '-'}</Typography>
                                  <Typography color="text.secondary" sx={{ fontSize: 12, textAlign: 'right' }}>{formatDashaDate(pratyantardasha.startUtc)} - {formatDashaDate(pratyantardasha.endUtc)}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : null}
              </Box>
            );
          }) : <Typography color="text.secondary">No Mahadasha periods available.</Typography>}
        </Stack>
      </CardContent>
    </Card>
  );
}

function KpDetailsCard({ chartData, language }: { chartData: any; language: string }) {
  const planetRows = useMemo(() => buildKpPlanetRows(chartData), [chartData]);
  const nadiRows = useMemo(() => buildNakshatraNadiRows(planetRows), [planetRows]);
  const houseRows = useMemo(() => buildKpHouseRows(chartData), [chartData]);
  const hasKpAnalysis = Boolean(chartData?.kpAnalysis);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{translateInline(language, 'KP Return Details')}</Typography>
        {!hasKpAnalysis ? (
          <Alert severity="info" sx={{ mb: 2 }}>{translateInline(language, 'KP sub-lords and significators are not available for this return chart.')}</Alert>
        ) : null}

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 800, mb: 1 }}>{translateInline(language, 'Planet RL / NL / SL')}</Typography>
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead><TableRow><TableCell>{translateInline(language, 'Planet')}</TableCell><TableCell>RL</TableCell><TableCell>NL</TableCell><TableCell>SL</TableCell><TableCell>SSL</TableCell><TableCell>{translateInline(language, 'Sig. Houses')}</TableCell></TableRow></TableHead>
          <TableBody>
            {planetRows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{shortPlanet(row.planet)}</TableCell>
                <TableCell>{row.rl || '-'}</TableCell>
                <TableCell>{row.nl || '-'}</TableCell>
                <TableCell>{row.sl || '-'}</TableCell>
                <TableCell>{row.ssl || '-'}</TableCell>
                <TableCell>{row.houses?.length ? row.houses.join(', ') : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 800, mb: 1 }}>{translateInline(language, 'Nakshatra Nadi')}</Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {nadiRows.length ? nadiRows.map((row: any) => (
            <Grid size={{ xs: 12, sm: 6 }} key={row.key}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{shortPlanet(row.planet)}{row.isUntenanted ? '*' : ''}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>{shortPlanet(row.planet)}:</strong> {row.planetSigs?.length ? row.planetSigs.join(' ') : '-'}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>{shortPlanet(row.starLord)}:</strong> {row.starLordSigs?.length ? row.starLordSigs.join(' ') : '-'}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>{shortPlanet(row.subLord)}:</strong> {row.subLordSigs?.length ? row.subLordSigs.join(' ') : '-'}</Typography>
              </Box>
            </Grid>
          )) : <Grid size={{ xs: 12 }}><Typography color="text.secondary">{translateInline(language, 'No Nakshatra Nadi data available.')}</Typography></Grid>}
        </Grid>
        {nadiRows.length ? <Typography color="text.secondary" sx={{ fontSize: 12, mb: 2 }}>{translateInline(language, '* Planet is not the Star Lord of any other planet.')}</Typography> : null}

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 800, mb: 1 }}>{translateInline(language, 'House Significators and Cusps')}</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>{translateInline(language, 'House')}</TableCell><TableCell>{translateInline(language, 'Degree')}</TableCell><TableCell>CL</TableCell><TableCell>NL</TableCell><TableCell>SL</TableCell><TableCell>SSL</TableCell><TableCell>{translateInline(language, 'Significators')}</TableCell></TableRow></TableHead>
          <TableBody>
            {houseRows.map((row) => (
              <TableRow key={row.house}>
                <TableCell>{row.house}</TableCell>
                <TableCell>{row.degree}</TableCell>
                <TableCell>{row.cuspLord}</TableCell>
                <TableCell>{row.starLord}</TableCell>
                <TableCell>{row.subLord}</TableCell>
                <TableCell>{row.subSubLord}</TableCell>
                <TableCell>{row.significators?.length ? row.significators.join(', ') : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function TithiPravesh() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useAuth();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isPremium = hasPremiumSubscription(state.user);
  const [year, setYear] = useState(String(currentYear));
  const [timeZone, setTimeZone] = useState('');
  const [kpReport, setKpReport] = useState<TpReport | null>(null);
  const [vedicReport, setVedicReport] = useState<TpReport | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const report = kpReport || vedicReport;
  const kpChart = useMemo(() => kpReport?.chart, [kpReport]);
  const vedicChart = useMemo(() => vedicReport?.chart, [vedicReport]);
  const vedicPlanets = useMemo(() => Object.entries(vedicReport?.chart?.planets || {}), [vedicReport]);

  const loadReport = async () => {
    const token = state.token || localStorage.getItem('nadiAstroToken') || '';
    if (!id || !token || !isPremium) return;
    try {
      setLoading(true);
      setError('');
      const fetchReport = async (model: 'KP' | 'VEDIC') => {
        const params = new URLSearchParams({ year, model });
        if (timeZone.trim()) params.set('timeZone', timeZone.trim());
        const response = await fetch(`${API_BASE_URL}/charts/${id}/tithi-pravesh?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || payload.error || translateInline(language, `Unable to calculate ${model} Tithi Pravesh`));
        return payload.data?.tithiPravesh || payload.tithiPravesh || payload.data;
      };

      const [kp, vedic] = await Promise.all([fetchReport('KP'), fetchReport('VEDIC')]);
      setKpReport(kp);
      setVedicReport(vedic);

      const chartResponse = await fetch(`${API_BASE_URL}/charts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const chartPayload = await chartResponse.json().catch(() => ({}));
      if (chartResponse.ok) {
        setChartData(chartPayload.data?.chart || chartPayload.chart || null);
      }
    } catch (err: any) {
      setError(err.message || translateInline(language, 'Unable to calculate Tithi Pravesh'));
      setKpReport(null);
      setVedicReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, state.token, isPremium]);

  if (!isPremium) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {id && <AnalysisTabBar chartId={id} active="tithiPravesh" />}
        <Alert
          severity="warning"
          action={<Button variant="contained" size="small" onClick={() => navigate('/upgrade')}>{translateInline(language, 'Upgrade')}</Button>}
        >
          {translateInline(language, 'Tithi Pravesh is available for Pro users.')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {id && <AnalysisTabBar chartId={id} active="tithiPravesh" />}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{translateInline(language, 'Tithi Pravesh')}</Typography>
          <Typography color="text.secondary">{translateInline(language, 'Annual Sun-Moon angular return calculated in UTC, displayed in local chart timezone.')}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label={translateInline(language, 'Year')} value={year} onChange={(e) => setYear(e.target.value)} size="small" sx={{ minWidth: 110 }} />
          <TextField label={translateInline(language, 'IANA timezone')} value={timeZone} onChange={(e) => setTimeZone(e.target.value)} size="small" placeholder="Asia/Kolkata" />
          <Button variant="contained" onClick={loadReport} disabled={loading}>{translateInline(language, 'Calculate')}</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}

      {report && !loading && (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            {[
              [translateInline(language, 'TP Local'), formatTithiDateTime(report.tpStartLocal, { keepSuffix: true })],
              [translateInline(language, 'TP UTC'), formatTithiDateTime(report.tpStartUtc, { utc: true })],
              [translateInline(language, 'Next TP Local'), formatTithiDateTime(report.nextTpStartLocal, { keepSuffix: true })],
              [translateInline(language, 'Timezone'), report.timeZoneUsed],
              [translateInline(language, 'Tithi'), `${translateInline(language, report.tithi?.name || '-')} (#${report.tithi?.number || '-'})`],
              [translateInline(language, 'Vara / Hora'), `${translateInline(language, report.vara?.hinduWeekday || '-')} · ${translateInline(language, report.vara?.horaLord || '-')} ${translateInline(language, 'Hora')}`],
            ].map(([label, value]) => (
              <Grid size={{ xs: 12, md: 4 }} key={label}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Chip label={`${translateInline(language, 'Method')}: ${report.method}`} />
                <Chip label={`${translateInline(language, 'Models')}: KP + Vedic`} />
                <Chip label={`${translateInline(language, 'Natal Sun-Moon angle')}: ${formatNumber(report.natalAngle)}°`} />
                <Chip label={`${translateInline(language, 'Location')}: ${report.locationUsed?.city || translateInline(language, 'Selected place')}`} />
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{translateInline(language, 'Sunrise-Based Vara and Hora')}</Typography>
              <Typography color="text.secondary">
                {translateInline(language, 'Sunrise')}: {formatTithiDateTime(report.vara?.sunriseLocal, { keepSuffix: true })} · {translateInline(language, 'Sunset')}: {formatTithiDateTime(report.vara?.sunsetLocal, { keepSuffix: true })} · {translateInline(language, 'Next sunrise')}: {formatTithiDateTime(report.vara?.nextSunriseLocal, { keepSuffix: true })}
              </Typography>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{translateInline(language, 'KP Tithi Pravesh Chart')}</Typography>
                  {kpChart ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <NorthIndianChart chartData={kpChart} size={420} showHouseLabels={false} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">{translateInline(language, 'KP Tithi Pravesh chart is not available.')}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{translateInline(language, 'Vedic Tithi Pravesh Chart')}</Typography>
                  {vedicChart ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <NorthIndianChart chartData={vedicChart} size={420} showHouseLabels={false} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">{translateInline(language, 'Vedic Tithi Pravesh chart is not available.')}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{translateInline(language, 'D1 Rashi Chart')}</Typography>
                  {chartData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <NorthIndianChart chartData={chartData} size={420} showHouseLabels={false} />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">{translateInline(language, 'Open or calculate a chart first to view D1.')}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AnnualDashaCard dasha={report.dasha || []} language={language} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <KpDetailsCard chartData={kpChart} language={language} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{translateInline(language, 'Vedic TP Chart Planets')}</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>{translateInline(language, 'Planet')}</TableCell><TableCell>{translateInline(language, 'Sign')}</TableCell><TableCell>{translateInline(language, 'Degree')}</TableCell><TableCell>{translateInline(language, 'Nakshatra')}</TableCell></TableRow></TableHead>
                    <TableBody>
                      {vedicPlanets.map(([key, planet]) => (
                        <TableRow key={key}>
                          <TableCell>{planet.name || key}</TableCell>
                          <TableCell>{planet.sign?.name || '-'}</TableCell>
                          <TableCell>{planet.degree?.formatted || `${formatNumber(planet.longitude)}°`}</TableCell>
                          <TableCell>{planet.nakshatra?.name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Container>
  );
}
